<template>
    <div v-if="isVisible || showNoSuggestionsHighlight || error" data-testid="profile-edition-section-skill-set-functional-skills">
        <FetchErrorHighlight v-if="error" data-testid="profile-edition-section-skill-set-functional-skills-error">
            <template #technical-error>{{ error }}</template>
        </FetchErrorHighlight>
        <VJoyText preset="primary-450" class="profile-edition-section-skill-set-functional-skills__title">
            {{ functionalSkillsLabel }}
        </VJoyText>
        <VJoyText preset="primary-300" class="profile-edition-section-skill-set-functional-skills__subtitle">
            {{ t('profile.section.skill.set.edition.functional.skills.subtitle') }}
            <VJoyLink class="profile-edition-section-skill-set-functional-skills__link" @click="openCategoriesEdit">
                {{ t('profile.section.skill.set.edition.functional.skills.subtitle.link.label') }}
            </VJoyLink>
        </VJoyText>
        <VJoyCollapse
            v-if="isVisible"
            :class="{'profile-edition-skill-set__functional-skills--invalid': showMinError}"
            mode="outline"
            :accordion="true"
            data-testid="profile-edition-section-skill-set-functional-scopes"
        >
            <VJoyCollapseItem
                v-for="(functionalScope, scopeIndex) in data?.functionalScopes"
                :key="`profile-edition-functional-scope-${scopeIndex}`"
                :header="functionalScope.label"
                :data-testid="`profile-edition-section-skill-set-functional-skills-scope-${scopeIndex}`"
                class="profile-edition-skill-set__functional-skills__item"
            >
                <template #collapse-header-right>
                    <VJoyTag
                        v-if="countSelectedFunctionalSkills(functionalScope) > 0"
                        :data-testid="`profile-edition-section-skill-set-functional-skills-scope-${scopeIndex}-counter`"
                    >
                        {{ countSelectedFunctionalSkills(functionalScope) }}
                    </VJoyTag>
                </template>
                <div
                    class="profile-edition-skills__list"
                    :data-testid="`profile-edition-section-skill-set-functional-skills-scope-${scopeIndex}-skills`"
                >
                    <VJoyTag
                        v-for="(skill, skillIndex) in functionalScope.functionalSkills"
                        :key="`profile-edition-section-skill-set-functional-skill-${skillIndex}`"
                        :data-testid="`profile-edition-section-skill-set-functional-skill-${skillIndex}`"
                        variant="secondary"
                        :selected="isFunctionalSkillAddedToSelectedSkills(skill)"
                        :selectable="isFunctionalSkillAddedToSelectedSkills(skill) || isFunctionalSkillAdditionEnabled"
                        @click="handleUpdate(skill)"
                    >
                        {{ skill.label }}
                    </VJoyTag>
                </div>
            </VJoyCollapseItem>
        </VJoyCollapse>
        <p
            v-if="showMinError"
            id="profile-edition-section-skill-set-functional-skills-min-error"
            class="profile-edition-skills__alert"
            data-testid="profile-edition-section-skill-set-functional-skills-min-error"
        >
            <VJoyIcon name="warning-triangle" size="xsmall" color="error" />
            {{ t('profile.section.skill.set.edition.functional.skills.count.error', [rulesFunctionalSkillsCountMin, rulesFunctionalSkillsCountMax]) }}
        </p>
        <VJoyHighlight
            v-if="showNoSuggestionsHighlight"
            level="neutral"
            display-icon
            class="profile-edition-skill-set__functional-skills__no-suggestions-highlight"
            data-testid="profile-edition-section-skill-set-functional-skills-no-suggestions-highlight"
        >
            {{ t('profile.section.skill.set.edition.functional.skills.no.suggestions.highlight') }}
        </VJoyHighlight>
    </div>
</template>
<script setup lang="ts">
    import {computed, ref, toRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useAsyncData, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyCollapse, VJoyHighlight, VJoyIcon, VJoyText, VJoyLink, VJoyCollapseItem, VJoyTag} from '@maltjoy/core-vue';
    import type {Skill} from '~/openapi';
    import {SkillTypeEnum} from '~/openapi';
    import {useProfileStore} from '~/store/profileStore';
    import type {FunctionalScope, FunctionalScopesAvailable, FunctionalSkill} from '~/types/domain/skill/Functional';
    import FetchErrorHighlight from '@/components/_common/FetchErrorHighlight.vue';

    const {t} = useTranslation();

    const props = defineProps<{
        modelValue: Skill[];
        isSkillAdditionEnabled?: boolean;
        rulesFunctionalSkillsCountMax: number;
        rulesFunctionalSkillsCountMin: number;
        showMinError?: boolean;
    }>();

    const isVisible = ref(false);
    const showNoSuggestionsHighlight = computed(() => !isVisible.value && !error.value);

    const unableToAddNewFunctionalSkills = computed(() => {
        return props.modelValue.length >= props.rulesFunctionalSkillsCountMax || !isFunctionalSkillAdditionEnabled.value;
    });

    const isFunctionalSkillAdditionEnabled = computed(() => {
        return props.isSkillAdditionEnabled && areFunctionalSkillsBelowMaxLimit.value;
    });

    const areFunctionalSkillsBelowMaxLimit = computed(() => {
        return props.modelValue.length < props.rulesFunctionalSkillsCountMax;
    });

    const emit = defineEmits<{
        add: [value: Skill];
        'update:modelValue': [value: Skill[]];
        remove: [value: Skill];
        'open-categories-edit': [];
    }>();

    const {profilePreferredCategory, profile} = storeToRefs(useProfileStore());
    const {listAllFunctionalScopes} = useProfileStore();

    const {data, error} = await useAsyncData(
        'edit_functional-skills-and-scopes',
        async () => {
            const functionalScopes: FunctionalScopesAvailable = await listAllFunctionalScopes();

            isVisible.value = functionalScopes.all?.length > 0;

            return {
                functionalScopes: functionalScopes.all,
            };
        },
        {deep: true},
    );

    const selectedAndTopFunctionnalSkills = toRef(props, 'modelValue');

    const functionalSkillsLabel = computed(() =>
        profilePreferredCategory.value && profilePreferredCategory.value.label
            ? t('profile.section.skill.set.edition.functional.skills.title', [profilePreferredCategory.value.label])
            : t('profile.section.skill.set.edition.functional.skills.title', [profile.value?.headline]),
    );

    function isFunctionalSkillAddedToSelectedSkills(functionalSkill: FunctionalSkill): boolean {
        return selectedAndTopFunctionnalSkills.value.findIndex((selectedSkillSetItem) => functionalSkill.id === selectedSkillSetItem.id) >= 0;
    }

    function countSelectedFunctionalSkills(functionalScope: FunctionalScope): number {
        return functionalScope.functionalSkills.filter(
            (functionalSkill) =>
                selectedAndTopFunctionnalSkills.value.findIndex((selectedSkillSetItem) => selectedSkillSetItem.id === functionalSkill.id) >= 0,
        ).length;
    }

    function handleUpdate(skillToUpdate: FunctionalSkill) {
        const skillSelected = isFunctionalSkillAddedToSelectedSkills(skillToUpdate);
        if (!skillSelected && !areFunctionalSkillsBelowMaxLimit.value) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.skill.set.edition.functional.skills.error.max.limit'),
                    level: 'error',
                },
            });
        } else if (!skillSelected && !isFunctionalSkillAdditionEnabled.value) {
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.skill.set.edition.error.max.limit'),
                    level: 'error',
                },
            });
        } else if (skillSelected) {
            handleRemove(skillToUpdate);
        } else {
            handleAdd(skillToUpdate);
        }
    }

    function handleAdd(skillToAdd: FunctionalSkill): void {
        const selectedSkillToAdd = toSkill(skillToAdd);
        const indexOfSelectedSkill = selectedAndTopFunctionnalSkills.value.findIndex(
            (selectedSkillSetItem) => selectedSkillSetItem.id === selectedSkillToAdd.id,
        );

        if (indexOfSelectedSkill < 0) {
            selectedAndTopFunctionnalSkills.value.push(selectedSkillToAdd);
        }

        emit('add', selectedSkillToAdd);
        emit('update:modelValue', selectedAndTopFunctionnalSkills.value);
    }

    function handleRemove(skillToRemove: FunctionalSkill): void {
        const selectedSkillToRemove = toSkill(skillToRemove);
        const indexOfSelectedSkill = selectedAndTopFunctionnalSkills.value.findIndex(
            (selectedSkillSetItem) => selectedSkillSetItem.id === selectedSkillToRemove.id,
        );

        if (indexOfSelectedSkill >= 0) {
            selectedAndTopFunctionnalSkills.value = selectedAndTopFunctionnalSkills.value.slice(indexOfSelectedSkill, 1);
        }

        emit('remove', selectedSkillToRemove);
        emit('update:modelValue', selectedAndTopFunctionnalSkills.value);
    }

    function openCategoriesEdit() {
        emit('open-categories-edit');
    }

    function toSkill(functionalSkill: FunctionalSkill): Skill {
        return {
            type: SkillTypeEnum.Functional,
            id: functionalSkill.id,
            label: functionalSkill.label,
        };
    }
</script>
<style lang="scss">
    .profile-edition-section-skill-set-functional-skills {
        &__subtitle {
            padding: var(--joy-core-spacing-2) 0 !important;
        }

        &__link {
            font-weight: var(--joy-font-weight-normal) !important;
        }
    }

    .profile-edition-skill-set {
        &__functional-skills {
            &__header {
                &__counter {
                    display: inline;
                }
            }

            &__item {
                .joy-collapse-item__header-right {
                    height: 1em;
                    display: flex;
                    align-items: center;
                }
            }

            &__highlight.joy-highlight {
                margin-bottom: var(--joy-core-spacing-4);
            }

            &__no-suggestions-highlight {
                margin-top: var(--joy-core-spacing-4);
            }

            &__alert {
                color: var(--joy-color-error-50) !important;
            }

            &--invalid {
                .joy-collapse-item {
                    border-color: var(--joy-color-error-50) !important;
                }
            }
        }
    }
</style>
